/**
 * Admin Maintenance - merge a duplicate leave type into the canonical one (super admin only).
 * Dry run first (requests, balances and exceptions per employee, conflicts), then apply with a
 * reason; every merge can be undone while nothing was booked against the merged balances.
 * Backend: routes/leave_type_merge.py, services/leave_type_merge.py.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { GitMerge, Loader2, Undo2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const err = (e) => e?.response?.data?.detail?.error || e?.message || 'Error';
const label = (t) => `${t.name || '—'}${t.key ? ` (${t.key})` : ' (no key)'} · ${t.id.slice(0, 8)}${t.is_active === false ? ' · inactive' : ''}`;

export default function LeaveTypeMergeCard({ token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [types, setTypes] = useState([]);
  const [source, setSource] = useState('');
  const [target, setTarget] = useState('');
  const [plan, setPlan] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [merges, setMerges] = useState([]);

  const load = useCallback(async () => {
    try {
      const [t, m] = await Promise.all([
        axios.get(`${API}/leave/types`, { headers, params: { active_only: false } }),
        axios.get(`${API}/admin/leave-type-merge`, { headers }),
      ]);
      setTypes(t.data.leave_types || []);
      setMerges(m.data.merges || []);
    } catch (e) { toast.error(err(e)); }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const dryRun = async () => {
    setBusy(true); setPlan(null);
    try { setPlan((await axios.post(`${API}/admin/leave-type-merge/dry-run`, { source_id: source, target_id: target }, { headers })).data); }
    catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/leave-type-merge/apply`, { source_id: source, target_id: target, reason }, { headers });
      toast.success(`Merged: ${r.data.totals.requests} request(s), ${r.data.totals.balance_rows} balance row(s)`);
      setPlan(null); setReason(''); load();
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  const undo = async (id) => {
    const why = window.prompt('Reason for undoing this merge');
    if (!why || why.trim().length < 3) return;
    try { await axios.post(`${API}/admin/leave-type-merge/${id}/undo`, { reason: why.trim() }, { headers }); toast.success('Merge undone'); load(); }
    catch (e) { toast.error(err(e)); }
  };

  return (
    <Card data-testid="leave-type-merge-card">
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><GitMerge className="h-4 w-4" />Merge a duplicate leave type</CardTitle></CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-slate-600">Moves every request, balance row and balance exception from the duplicate onto the canonical type,
          adds used and pending days where both have a balance (the canonical allocation is kept: never two allocations),
          and deactivates the duplicate. Refused when a merged balance would exceed the yearly allocation.</p>
        <div className="grid md:grid-cols-2 gap-2">
          <label className="space-y-1"><span className="text-xs text-slate-500">Duplicate (source)</span>
            <select className="w-full border rounded px-2 py-1" value={source} onChange={(e) => { setSource(e.target.value); setPlan(null); }} data-testid="merge-source">
              <option value="">—</option>{types.map((t) => <option key={t.id} value={t.id}>{label(t)}</option>)}
            </select></label>
          <label className="space-y-1"><span className="text-xs text-slate-500">Canonical (target)</span>
            <select className="w-full border rounded px-2 py-1" value={target} onChange={(e) => { setTarget(e.target.value); setPlan(null); }} data-testid="merge-target">
              <option value="">—</option>{types.map((t) => <option key={t.id} value={t.id}>{label(t)}</option>)}
            </select></label>
        </div>
        <Button size="sm" variant="outline" disabled={!source || !target || source === target || busy} onClick={dryRun} data-testid="merge-dry-run">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Dry run'}</Button>

        {plan && (
          <div className="space-y-2 border rounded p-3 bg-slate-50" data-testid="merge-plan">
            <div>{plan.totals.employees} employee(s) · {plan.totals.requests} request(s) · {plan.totals.balance_rows} balance row(s) · {plan.totals.exceptions} exception(s)
              {plan.totals.conflicts > 0 && <Badge className="ms-2 bg-rose-100 text-rose-700">{plan.totals.conflicts} conflict(s)</Badge>}</div>
            <div className="max-h-72 overflow-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-slate-500"><th className="text-start">Employee</th><th>Requests</th><th>Exceptions</th><th className="text-start">Balances (year: source → merged)</th></tr></thead>
                <tbody>{plan.employees.map((e) => (
                  <tr key={e.employee_id} className={`border-t ${e.conflicts.length ? 'bg-rose-50' : ''}`}>
                    <td>{e.name || e.employee_id}{e.employee_number ? ` · ${e.employee_number}` : ''}</td>
                    <td className="text-center">{e.requests} ({e.request_days} d)</td>
                    <td className="text-center">{e.exceptions}</td>
                    <td>{e.balances.map((b) => (
                      <div key={b.year}>{b.year}: used {b.source.used_days}/pending {b.source.pending_days} → allocated {b.merged.allocated_days}, used {b.merged.used_days}, pending {b.merged.pending_days}{b.target ? ' (added to the existing row)' : ' (moved)'}</div>
                    ))}
                    {e.conflicts.map((c) => <div key={c.year} className="text-rose-700">over the allocation by {c.over_by} day(s) in {c.year}</div>)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {plan.can_apply ? (
              <div className="space-y-2">
                <Textarea placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="merge-reason" />
                <Button size="sm" disabled={busy || reason.trim().length < 3} onClick={apply} data-testid="merge-apply">Apply merge</Button>
              </div>
            ) : <p className="text-rose-700">Fix the conflicting balances first; the merge is refused while they exceed the allocation.</p>}
          </div>
        )}

        {merges.length > 0 && (
          <div className="space-y-1" data-testid="merge-history">
            <div className="text-xs text-slate-500">Past merges</div>
            {merges.map((m) => (
              <div key={m.id} className="flex items-center gap-2 text-xs border-t pt-1">
                <span>{String(m.applied_at).slice(0, 16).replace('T', ' ')} · {m.source_id.slice(0, 8)} → {m.target_id.slice(0, 8)} · {m.totals?.requests} request(s)</span>
                <Badge variant="outline">{m.status}</Badge>
                {m.status === 'applied' && <Button size="sm" variant="ghost" onClick={() => undo(m.id)}><Undo2 className="h-3 w-3 me-1" />Undo</Button>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
